/**
 * <ConfirmDialog> — die Rueckfrage im Design der Anwendung (Issue #67).
 *
 * Loest die Systemdialoge des Browsers ab. Die Aufrufstelle soll dadurch NICHT
 * umstaendlicher werden als vorher, deshalb liefert `useConfirm()` eine Funktion,
 * die ein Promise auf `true`/`false` zurueckgibt:
 *
 *     const confirmAction = useConfirm();
 *     …
 *     onClick={() => {
 *       void confirmAction({
 *         title: t('…'),
 *         tone: 'danger',
 *         confirmLabel: t('common.delete', 'Löschen'),
 *       }).then((ok) => { if (ok) remove.mutate(id); });
 *     }}
 *
 * Die Funktion heisst bewusst nicht `confirm` — so kann der Waechtertest
 * `src/test/no-system-dialogs.test.ts` jeden Aufruf der Systemdialoge `confirm`
 * und `alert` im Quelltext verbieten, ohne die eigene Rueckfrage mitzutreffen.
 *
 * `tone: 'danger'` ist fuer zerstoererische Vorgaenge (Loeschen). Der Dialog
 * zeigt dann ein anderes Zeichen und eine rote Bestaetigungsschaltflaeche —
 * eine Loeschbestaetigung soll nicht aussehen wie ein „Fortfahren?".
 */
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useId,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './modal.js';

export type ConfirmTone = 'default' | 'danger';

export type ConfirmOptions = {
  /** Kurze Frage. Beschriftet den Dialog auch fuer Screenreader. */
  title: string;
  /** Optionale Erlaeuterung — was passiert, was bleibt. Zeilenumbrueche bleiben erhalten. */
  body?: string | undefined;
  /** Beschriftung der bestaetigenden Schaltflaeche. Default: „Bestätigen". */
  confirmLabel?: string | undefined;
  /** Beschriftung der abbrechenden Schaltflaeche. Default: „Abbrechen". */
  cancelLabel?: string | undefined;
  /** `danger` fuer zerstoererische Vorgaenge. Default: `default`. */
  tone?: ConfirmTone | undefined;
};

export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  /**
   * Der offene `resolve` liegt in einer Ref, nicht im State: ihn im State-Updater
   * aufzurufen waere ein Nebeneffekt in einer Funktion, die React (StrictMode)
   * doppelt ausfuehren darf.
   */
  const resolveRef = useRef<((answer: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        // Eine zweite Rueckfrage waehrend einer offenen beantwortet die erste mit
        // „abgebrochen" — sonst bliebe deren Promise fuer immer offen.
        resolveRef.current?.(false);
        resolveRef.current = resolve;
        setPending(options);
      }),
    [],
  );

  const settle = useCallback((answer: boolean) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setPending(null);
    resolve?.(answer);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending !== null && <ConfirmDialog options={pending} onSettle={settle} />}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error('useConfirm outside ConfirmProvider');
  return context;
}

export type ConfirmDialogProps = {
  options: ConfirmOptions;
  onSettle: (answer: boolean) => void;
};

export function ConfirmDialog({ options, onSettle }: ConfirmDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const isDanger = (options.tone ?? 'default') === 'danger';

  return (
    <Modal
      open
      onClose={() => onSettle(false)}
      labelledBy={titleId}
      panelClassName="w-full max-w-md overflow-hidden"
    >
      <div className="flex items-start gap-3 px-5 pt-5 pb-4">
        {/*
          Das Zeichen ist reine Verstaerkung des Tons — die Aussage steht im Text,
          deshalb `aria-hidden`. Rot fuer zerstoererisch, sonst die Primaerfarbe.
        */}
        <span
          aria-hidden="true"
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
          style={{
            background: isDanger ? 'var(--danger-soft)' : 'var(--primary-soft)',
            color: isDanger ? 'var(--danger)' : 'var(--primary)',
          }}
        >
          {isDanger ? '!' : '?'}
        </span>
        <div className="min-w-0">
          <h2
            id={titleId}
            className="text-sm font-medium"
            style={{ color: 'var(--ink)', fontFamily: 'var(--f-display)' }}
          >
            {options.title}
          </h2>
          {options.body && (
            <p
              className="mt-1.5 text-sm whitespace-pre-line"
              style={{ color: 'var(--ink-2)', lineHeight: 1.5 }}
            >
              {options.body}
            </p>
          )}
        </div>
      </div>
      <footer
        className="flex items-center justify-end gap-2 border-t px-5 py-3"
        style={{ borderColor: 'var(--line-soft)', background: 'var(--surface-2)' }}
      >
        {/* Abbrechen steht zuerst im Baum — es bekommt damit den Anfangsfokus. */}
        <button
          type="button"
          onClick={() => onSettle(false)}
          className="rounded-md px-3 py-1.5 text-xs"
          style={{
            background: 'var(--surface)',
            color: 'var(--ink-2)',
            border: '1px solid var(--line-soft)',
            cursor: 'pointer',
          }}
        >
          {options.cancelLabel ?? t('v2.modal.cancel', 'Abbrechen')}
        </button>
        <button
          type="button"
          onClick={() => onSettle(true)}
          className="rounded-md px-3 py-1.5 text-xs font-medium"
          style={{
            background: isDanger ? 'var(--danger)' : 'var(--primary)',
            color: isDanger ? '#ffffff' : 'var(--on-primary)',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          {options.confirmLabel ?? t('v2.confirm.ok', 'Bestätigen')}
        </button>
      </footer>
    </Modal>
  );
}
